/* eslint-disable no-plusplus */
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import instance from "services/axiosServices";
import backofficeEnviroment from "utils/backofficeEnviroment";

export interface ExportarKanbanParams {
  ListaCodigo: string[];
  Comercial: boolean;
  tipoFiltroData?: number | null;
  dataInicio?: string;
  dataFim?: string;
  Etapa?: string;
  SubEtapa?: string[];
  IdEmpreendimento?: string[];
  IdBloco?: string;
  IdUnidade?: string;
  IdsOrigens?: string[];
  IdSuperintendente365?: string;
  IdGerente365?: string;
  IdCorretor365?: string;
  Compartilhadas?: boolean;
  PalavraChave?: string;
  PaginaAtual?: number;
  TotalItensRetorno?: number;
}

export function useExportarKanban(comercial: boolean) {
  const buildQueryString = (params: ExportarKanbanParams) => {
    const searchParams = new URLSearchParams();
    const hasDateFilter =
      params.tipoFiltroData !== null &&
      params.tipoFiltroData !== undefined &&
      !!params.dataInicio &&
      !!params.dataFim;

    params.ListaCodigo.forEach((codigo) => {
      searchParams.append("ListaCodigo", codigo);
    });

    searchParams.append("Comercial", String(params.Comercial));

    if (params.Etapa) {
      searchParams.append("Etapa", params.Etapa);
    }

    if (hasDateFilter) {
      searchParams.append("tipoFiltroData", String(params.tipoFiltroData));
    }

    if (hasDateFilter) {
      searchParams.append("dataInicio", params.dataInicio || "");
      searchParams.append("dataFim", params.dataFim || "");
    }

    if (params.SubEtapa && params.SubEtapa.length > 0) {
      params.SubEtapa.forEach((sub) => {
        searchParams.append("SubEtapa", String(sub));
      });
    }

    if (params.IdEmpreendimento && params.IdEmpreendimento.length > 0) {
      params.IdEmpreendimento.forEach((emp) => {
        searchParams.append("IdEmpreendimento", emp);
      });
    }

    if (params.IdBloco) {
      searchParams.append("IdBloco", params.IdBloco);
    }

    if (params.IdUnidade) {
      searchParams.append("IdUnidade", params.IdUnidade);
    }

    if (params.IdsOrigens && params.IdsOrigens.length > 0) {
      params.IdsOrigens.forEach((origem) => {
        searchParams.append("IdsOrigens", origem);
      });
    }

    if (params.IdSuperintendente365) {
      searchParams.append("IdSuperintendente365", params.IdSuperintendente365);
    }

    if (params.IdGerente365) {
      searchParams.append("IdGerente365", params.IdGerente365);
    }

    if (params.IdCorretor365) {
      searchParams.append("IdCorretor365", params.IdCorretor365);
    }

    if (params.Compartilhadas) {
      searchParams.append("Compartilhadas", String(params.Compartilhadas));
    }

    if (params.PalavraChave) {
      searchParams.append("PalavraChave", params.PalavraChave);
    }

    if (params.PaginaAtual) {
      searchParams.append("PaginaAtual", String(params.PaginaAtual));
    }

    if (params.TotalItensRetorno) {
      searchParams.append(
        "TotalItensRetorno",
        String(params.TotalItensRetorno)
      );
    }

    return searchParams.toString();
  };

  const exportData = async (params: ExportarKanbanParams) => {
    const url = `${backofficeEnviroment}/common/backoffice/exportar-kanban/v1?${buildQueryString(
      params
    )}`;
    const response = await instance.get(url);
    return response?.data;
  };

  return useMutation({
    mutationFn: exportData,
    mutationKey: ["exportar-kanban", comercial],
    onSuccess: (data: any) => {
      const base64 = data?.data?.base64Planilha;
      if (!base64) {
        toast.error("Nenhum dado retornado para exportação.");
        return;
      }

      const byteCharacters = atob(base64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const now = new Date();
      const dateStr = now.toISOString().split("T")[0];
      const tipo = comercial ? "funil-comercial" : "funil-propostas";
      const fileName = `${tipo}-${dateStr}.xlsx`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Planilha exportada com sucesso!");
    },
    onError: (error: any) => {
      toast.error(
        `Erro ao exportar planilha. Motivos:\n ${
          error?.response?.data?.notifications?.map(
            (item: any) => `${item?.message}`
          ) || "Erro desconhecido"
        }`,
        { autoClose: 10000 }
      );
    },
  });
}
